import React, { useEffect, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { useLocation } from 'react-router';
import { ErrorModel, ProfileReadExternal, ProfileUpdateExternal } from '@askporter/client-grieg-lyric';
import { Dialog, useToast, Theme } from '@askporter/component-library';
import { useTranslation } from '@askporter/config-provider';
import { useProfile } from '@askporter/profile-provider';
import { API, getLocalStorageCookieConsent, setLocalStorageCookieConsent } from '../../utils';
import CustomiseView from './CustomiseView';
import InitialView from './InitialView';
interface CookieManagementProps {
  children: React.ReactNode | React.ReactNode[];
}
interface CookiePreferences {
  rum: boolean;
}
const CookieManagement: React.FC<React.PropsWithChildren<CookieManagementProps>> = ({
  children,
}: CookieManagementProps) => {
  const { t } = useTranslation();
  const isSmallDevice = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { showError } = useToast();
  const queryClient = useQueryClient();
  const { data: profileData, isLoading: profileIsLoading } = useProfile();
  const { pathname } = useLocation();
  const [dialogView, setDialogView] = useState<'INITIAL' | 'CUSTOMISE'>('INITIAL');
  const [cookies, setCookies] = useState<CookiePreferences>({ rum: true });
  const [hasSavedConsent, setHasSavedConsent] = useState(false);

  const profileHasConsent: boolean =
    typeof profileData?.consent?.cookieConsent === 'boolean' && !!profileData?.consent?.cookieConsentLastUpdated;
  const localStorageConsent = getLocalStorageCookieConsent();
  const hasAlreadySetCookiePreference: boolean = hasSavedConsent || profileHasConsent || localStorageConsent !== null;
  const toggleCookiePreference = (cookie: keyof CookiePreferences) => {
    setCookies((prev) => ({ ...prev, [cookie]: !prev[cookie] }));
  };
  const profileAPIPath = 'profile';
  const mutateProfile = useMutation<AxiosResponse<ProfileReadExternal>, AxiosError<ErrorModel>, ProfileUpdateExternal>({
    mutationFn: (payload: ProfileUpdateExternal) => API().post({ path: profileAPIPath, payload }),
    onMutate: async (profileUpdate) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: [profileAPIPath] });
      // Snapshot the previous value
      const previousProfileData: ProfileReadExternal = queryClient.getQueryData([profileAPIPath]);
      // Optimistically update to the new value
      queryClient.setQueryData([profileAPIPath], (old: ProfileReadExternal) => ({ ...old, ...profileUpdate }));
      // Return a context object with the snapshotted value
      return previousProfileData;
    },
    // If the mutation fails, use the context returned from onMutate to roll back
    // and refetch to ensure the cache reflects the real server state
    onError: (_err, _assetUpdate, context: ProfileReadExternal) => {
      showError(t('ns.common:error:problem'));
      queryClient.setQueryData([profileAPIPath], context);
      queryClient.invalidateQueries({ queryKey: [profileAPIPath] });
    },
  });
  const updateProfile = (cookieConsent: boolean) => {
    setLocalStorageCookieConsent(cookieConsent);
    setHasSavedConsent(true);
    if (profileData?.uid) {
      const consent = { cookieConsent, cookieConsentLastUpdated: new Date().toISOString() };
      mutateProfile.mutate({ consent });
    }
  };

  // When a user logs in after setting consent pre-login via localStorage, sync that consent to their profile
  useEffect(() => {
    const localConsent = getLocalStorageCookieConsent();
    const hasProfileConsent =
      typeof profileData?.consent?.cookieConsent === 'boolean' && !!profileData?.consent?.cookieConsentLastUpdated;
    if (profileData?.uid && !hasProfileConsent && localConsent !== null) {
      mutateProfile.mutate({
        consent: {
          cookieConsent: localConsent.cookieConsent,
          cookieConsentLastUpdated: localConsent.cookieConsentLastUpdated,
        },
      });
    }
    // mutateProfile.mutate is stable in React Query v5; including the full mutateProfile object
    // would cause this effect to re-fire on every mutation state transition, triggering duplicate API calls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileData?.uid, profileData?.consent, mutateProfile.mutate]);

  const handleAcceptAllOnClick = () => {
    const allAccepted = (Object.keys(cookies) as (keyof CookiePreferences)[]).reduce<CookiePreferences>(
      (acc, key) => ({ ...acc, [key]: true }),
      cookies,
    );
    setCookies(allAccepted);
    updateProfile(allAccepted.rum);
  };
  const handleSaveOnClick = () => {
    updateProfile(cookies.rum);
  };

  const isUnauthenticatedWebchat =
    !profileData?.uid && (pathname === '/guest' || pathname.startsWith('/tchat') || pathname.startsWith('/onsite'));

  if (
    hasAlreadySetCookiePreference ||
    profileIsLoading ||
    pathname.includes('/verify-number') ||
    isUnauthenticatedWebchat
  )
    return <>{children}</>;
  else
    return (
      <>
        {children}
        <Dialog isOpen={true} variant="banner">
          {dialogView === 'INITIAL' ? (
            <InitialView
              t={t}
              setDialogView={setDialogView}
              handleAcceptAllOnClick={handleAcceptAllOnClick}
              isSmallDevice={isSmallDevice}
            />
          ) : null}
          {dialogView === 'CUSTOMISE' ? (
            <CustomiseView
              t={t}
              cookies={cookies}
              toggleCookiePreference={toggleCookiePreference}
              setDialogView={setDialogView}
              handleSaveOnClick={handleSaveOnClick}
            />
          ) : null}
        </Dialog>
      </>
    );
};
export default CookieManagement;
